'use client';

import type {
  PropsWithChildren} from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import classNames from 'classnames';

import { Karma2 } from '@ui/uikit/components/icons/mono/Karma2';
import type { CssMeasureAbsolute,CssMeasureRelative } from '@utilities/helpers/css';

import { Button } from '../../Button';
import { FormInput } from '../../FormInput';
import styles from './NewsletterSubscription.module.scss';

interface NewsletterSubscriptionContextValue {
  subscribe(email: string): Promise<void>;
}

const NewsletterSubscriptionContext = createContext<NewsletterSubscriptionContextValue>({
  subscribe: () => {
    throw new Error('NewsletterSubscription.Provider is not available.');
  },
});

const NewsletterSubscriptionProvider = ({
  children,
  ...rest
}: PropsWithChildren<NewsletterSubscriptionContextValue>) => {
  return (
    <NewsletterSubscriptionContext.Provider value={rest}>
      {children}
    </NewsletterSubscriptionContext.Provider>
  );
};

const EMAIL_PATTERN = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;

interface NewsletterSubscriptionWidgetProps {
  width?: CssMeasureAbsolute | CssMeasureRelative;
}

const NewsletterSubscriptionWidget = ({ width }: NewsletterSubscriptionWidgetProps) => {
  const { subscribe } = useContext(NewsletterSubscriptionContext);

  const [email, setEmail] = useState<string>();
  const [submissionAttempted, setSubmissionAttempted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleButtonClick = useCallback(async () => {
    setSubmissionAttempted(true);

    if (email && !error) {
      try {
        await subscribe(email);

        setEmail('');
        setError(null);
      } catch (err) {
        console.log(`Failed to subscribe to newsletter with e-mail '${email}'.`, err);
      }
    }
  }, [subscribe, email, error]);

  useEffect(() => {
    if (email && !EMAIL_PATTERN.test(email)) {
      setError('Please, enter a valid e-mail address');
    } else {
      setError(null);
    }
  }, [email]);

  return (
    <div className={classNames(styles['container'])} style={{ width }}>
      <div>
        <div className={classNames(styles['text'])}>
          <span>Unlock</span>
          <Karma2 width={16} />
          <span>Exclusive Perks</span>
        </div>

        <div className={classNames(styles['subtext'])}>Subscribe &amp; Empower Your Future!</div>
      </div>

      <div className={classNames(styles['body'])}>
        <FormInput
          placeholder="Email"
          value={email}
          onValueChange={setEmail}
          isError={submissionAttempted && !!error}
          hint={(submissionAttempted && error) || '\u200B'}
          afterInput={
            <Button
              variant="secondary"
              size="large"
              disabled={!email || (submissionAttempted && !!error)}
              onClick={handleButtonClick}
            >
              Subscribe
            </Button>
          }
        />
      </div>
    </div>
  );
};

const NewsletterSubscription = {
  Widget: NewsletterSubscriptionWidget,
  Provider: NewsletterSubscriptionProvider,
} as const;

export {
  NewsletterSubscription,
  NewsletterSubscriptionWidget,
  type NewsletterSubscriptionContextValue,
  NewsletterSubscriptionProvider as NewsletterSubscriptionContextProvider,
};
